import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type {
  ImageLibraryFeatureFlags,
  ImageLibraryNavigationItem,
  ImageLibraryStep,
  ImageLibraryMode,
} from 'hb-react/backoffice/components/ImageLibrary/types';

import { getNavigationItems } from '../utils/getNavigationItems';

type ActiveItem = Partial<ImageLibraryNavigationItem> | null;

interface Mode {
  set?: ActiveItem;
}

export interface NavigationItemProviderProps {
  children: React.ReactNode;
  features: ImageLibraryFeatureFlags;
  updateModalTitle: (title: string) => void;
}

interface NavigationItemProviderValue {
  activeItem?: ActiveItem;
  setActiveItem: Dispatch<SetStateAction<ActiveItem | undefined>>;

  goToSet: () => void;
  goToCrop: () => void;
  goToPreview: () => void;
  currentStep: ImageLibraryStep;

  navigationItems: ImageLibraryNavigationItem[] | undefined;

  mode: Mode;
  handleSetMode: (arg: ImageLibraryMode) => void;
}

const NavigationItemContext = createContext<NavigationItemProviderValue>({
  activeItem: undefined,
  setActiveItem: () => {},

  goToSet: () => {},
  goToCrop: () => {},
  goToPreview: () => {},
  currentStep: 'set',

  navigationItems: undefined,

  mode: {},
  handleSetMode: () => {},
});

const STEPS = Object.freeze({
  set: 'set',
  crop: 'crop',
  preview: 'preview',
});

const stepsArray = Object.values(STEPS);

export const NavigationItemProvider = ({
  children,
  features,
  updateModalTitle,
}: NavigationItemProviderProps) => {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<Mode>({});
  const [activeItem, setActiveItem] = useState<ActiveItem>();

  const currentStep = useMemo(() => stepsArray[step], [step]);

  const navigationItems = useMemo(
    () => getNavigationItems({ features, step: currentStep }),
    [features, currentStep],
  );

  const goToSet = useCallback(() => {
    const index = stepsArray.indexOf(STEPS.set);

    setActiveItem(mode.set);
    setStep(index);
  }, [mode.set]);

  const goToCrop = useCallback(() => {
    const index = stepsArray.indexOf(STEPS.crop);

    setActiveItem({ id: STEPS.crop });
    setStep(index);
  }, []);

  const goToPreview = useCallback(() => {
    const index = stepsArray.indexOf(STEPS.preview);

    setActiveItem(null);
    setStep(index);
  }, []);

  const handleSetMode = useCallback(
    (newMode) =>
      setMode((mode) => ({
        ...mode,
        set:
          navigationItems?.find((item) => item.id === newMode) ||
          navigationItems?.[0],
      })),
    [navigationItems],
  );

  useEffect(() => {
    setActiveItem(navigationItems?.[0]);
  }, [navigationItems]);

  useEffect(() => {
    if (step === 0) {
      updateModalTitle?.(__('Image Library'));
    } else {
      updateModalTitle?.(__('Image Settings'));
    }
  }, [step, updateModalTitle]);

  const value = useMemo(
    () => ({
      activeItem,
      setActiveItem,
      goToSet,
      goToCrop,
      goToPreview,
      currentStep,
      navigationItems,
      mode,
      handleSetMode,
    }),
    [
      activeItem,
      setActiveItem,

      goToSet,
      goToCrop,
      goToPreview,
      currentStep,

      navigationItems,

      mode,
      handleSetMode,
    ],
  );

  return (
    <NavigationItemContext.Provider value={value}>
      {children}
    </NavigationItemContext.Provider>
  );
};

export const useNavigationItemContext = () => useContext(NavigationItemContext);

export default NavigationItemContext;
